/**
 * Shared parsing for numbered reference lists — IEEE, Vancouver and AMA — and the
 * list-level checks those styles have in common.
 *
 * All three number references in the order they are first cited, so a list is a
 * sequence of `[1] …` / `1. …` entries rather than an alphabetical one. This module
 * splits a pasted list into entries, keeps the number each entry was given, and checks
 * the numbering itself (gaps, repeats, numbers out of sequence), duplicate sources and
 * the tell-tale alphabetical order of a list sorted the author–date way.
 *
 * Like the original APA/MLA/Chicago/Harvard linters, nothing here touches the citation
 * core (`src/lib/citation.ts`, `src/lib/bibliography.ts`): string in, findings out. A
 * finding carries a rule id and the values its message needs; the words live in the
 * widget's strings so a translated page can say them.
 */

export type Confidence = "high" | "medium";

/** One likely issue. `R` is the checker's rule union, `S` its source keys. */
export interface Finding<R extends string = string, S extends string = string> {
  rule: R;
  confidence: Confidence;
  /** The guideline that explains the rule — a key into the checker's `SOURCES`. */
  source: S;
  /** Values the widget interpolates into the rule's message. */
  values?: Record<string, string | number>;
  /** A corrected fragment the reader can copy. */
  suggestion?: string;
}

export interface EntryReport<
  R extends string = string,
  S extends string = string,
> {
  /** 1-based position in the pasted list. */
  position: number;
  /** The number the writer gave the entry ([3] → 3), or null when it has none. */
  label: number | null;
  /** The entry without its number, on one line. */
  text: string;
  findings: Finding<R, S>[];
}

export interface CheckReport<
  R extends string = string,
  S extends string = string,
> {
  entries: EntryReport<R, S>[];
  /** Findings about the list as a whole: numbering, order, duplicates. */
  listFindings: Finding<R, S>[];
  totalEntries: number;
  totalFindings: number;
}

/** How an entry's number was written. */
export type NumberMarker = "bracket" | "period" | "paren" | "close" | "bare";

export interface ListEntry {
  position: number;
  label: number | null;
  marker: NumberMarker | null;
  text: string;
}

/** The list-level rules every numbered checker shares. */
export type ListRule =
  | "unnumbered"
  | "numbering-missing"
  | "numbering-duplicate"
  | "numbering-start"
  | "numbering-gap"
  | "numbering-order"
  | "duplicate-entry"
  | "alphabetical-order";

// --- splitting -------------------------------------------------------------------

const RE_NEWLINE = /\r?\n/;
const RE_SPACES = /[ \t ]+/g;
/*
 * One to three digits (four inside brackets). A hanging-indent line that wraps can
 * begin with the year — "2019. [Online]. Available: …" — and a four-digit "number"
 * there would turn the rest of a reference into reference 2019 and report two
 * thousand missing ones. Lists of a thousand references do not occur in practice.
 */
const RE_BRACKET_MARKER = /^\[(\d{1,4})\]\s*/;
const RE_PAREN_MARKER = /^\((\d{1,3})\)\s*/;
const RE_PERIOD_MARKER = /^(\d{1,3})\.(?=\s)\s*/;
const RE_CLOSE_MARKER = /^(\d{1,3})\)(?=\s)\s*/;
const RE_BARE_MARKER = /^(\d{1,3})(?:\t| {2,}| (?=[\p{Lu}[“"‘(]))\s*/u;
const RE_LIST_HEADING =
  /^(?:references?|reference list|bibliography|works cited|literature cited|sources)\s*:?$/i;

interface MarkerMatch {
  label: number;
  marker: NumberMarker;
  rest: string;
}

const MARKERS: readonly [RegExp, NumberMarker][] = [
  [RE_BRACKET_MARKER, "bracket"],
  [RE_PAREN_MARKER, "paren"],
  [RE_PERIOD_MARKER, "period"],
  [RE_CLOSE_MARKER, "close"],
  [RE_BARE_MARKER, "bare"],
];

function matchMarker(line: string): MarkerMatch | null {
  for (const [re, marker] of MARKERS) {
    const match = re.exec(line);
    if (match) {
      return {
        label: Number(match[1]),
        marker,
        rest: line.slice(match[0].length),
      };
    }
  }
  return null;
}

function oneLine(text: string): string {
  return text.replace(RE_SPACES, " ").trim();
}

interface RawLine {
  text: string;
  blankBefore: boolean;
  marker: MarkerMatch | null;
}

function readLines(text: string): RawLine[] {
  const lines: RawLine[] = [];
  let blank = false;
  for (const raw of text.split(RE_NEWLINE)) {
    const line = oneLine(raw);
    if (line === "") {
      blank = true;
      continue;
    }
    if (lines.length === 0 && RE_LIST_HEADING.test(line)) {
      continue;
    }
    lines.push({ text: line, blankBefore: blank, marker: matchMarker(line) });
    blank = false;
  }
  // A bare "12 " prefix is only a number when the list numbers that way on purpose;
  // one stray line that starts with a figure is more likely text.
  const bare = lines.filter((line) => line.marker?.marker === "bare").length;
  if (bare < 2) {
    clearMarkers(lines, (line) => line.marker?.marker === "bare");
  }
  // One marker, and not on the first line, is a stray "[4]" in an unnumbered list —
  // reading it as numbering would glue every other reference onto its neighbour.
  const marked = lines.filter((line) => line.marker);
  if (marked.length === 1 && marked[0] !== lines[0]) {
    clearMarkers(lines, () => true);
  }
  return lines;
}

function clearMarkers(
  lines: RawLine[],
  which: (line: RawLine) => boolean
): void {
  for (const line of lines) {
    if (which(line)) {
      line.marker = null;
    }
  }
}

/** Without numbers: blank-line blocks are entries, otherwise each line is one. */
function splitUnnumbered(lines: RawLine[]): string[] {
  const hasBlocks = lines.some((line, index) => index > 0 && line.blankBefore);
  if (!hasBlocks) {
    return lines.map((line) => line.text);
  }
  const blocks: string[] = [];
  for (const line of lines) {
    if (line.blankBefore || blocks.length === 0) {
      blocks.push(line.text);
    } else {
      blocks[blocks.length - 1] = `${blocks.at(-1)} ${line.text}`;
    }
  }
  return blocks;
}

/**
 * Split a pasted list into entries, keeping each entry's number.
 *
 * A line that starts with a number marker — `[3]`, `3.`, `(3)`, `3)` or, when the
 * whole list does it, a bare `3` — starts an entry; the lines after it are the same
 * entry wrapped by a hanging indent. A list with no numbers at all is read one entry
 * per line, or one per blank-line block.
 */
export function parseNumberedList(text: string): ListEntry[] {
  const lines = readLines(text);
  if (lines.length === 0) {
    return [];
  }
  if (!lines.some((line) => line.marker)) {
    return splitUnnumbered(lines).map((entry, index) => ({
      position: index + 1,
      label: null,
      marker: null,
      text: entry,
    }));
  }
  const entries: Omit<ListEntry, "position">[] = [];
  for (const line of lines) {
    const current = entries.at(-1);
    if (line.marker) {
      entries.push({
        label: line.marker.label,
        marker: line.marker.marker,
        text: line.marker.rest,
      });
    } else if (current && !line.blankBefore) {
      current.text = `${current.text} ${line.text}`;
    } else {
      entries.push({ label: null, marker: null, text: line.text });
    }
  }
  return entries.map((entry, index) => ({
    ...entry,
    text: oneLine(entry.text),
    position: index + 1,
  }));
}

/** "3" — how a finding names an entry: its own number, else its position. */
export function entryName(
  entry: Pick<ListEntry, "label" | "position">
): string {
  return String(entry.label ?? entry.position);
}

// --- list-level checks -----------------------------------------------------------

/** "3, 4, 5" — ASCII digits: these are labels, not quantities. */
export function numberList(numbers: readonly number[], max = 12): string {
  const shown = numbers.slice(0, max).map(String);
  return numbers.length > max ? `${shown.join(", ")}, …` : shown.join(", ");
}

function sequenceFindings<S extends string>(
  numbered: readonly ListEntry[],
  source: S
): { findings: Finding<ListRule, S>[]; seen: Set<number> } {
  const findings: Finding<ListRule, S>[] = [];
  const seen = new Set<number>();
  let previous: number | null = null;
  for (const entry of numbered) {
    const label = entry.label as number;
    if (seen.has(label)) {
      findings.push({
        rule: "numbering-duplicate",
        confidence: "high",
        source,
        values: { number: label },
      });
    } else if (previous !== null && label < previous) {
      findings.push({
        rule: "numbering-order",
        confidence: "high",
        source,
        values: { number: label, previous },
      });
    }
    seen.add(label);
    previous = label;
  }
  return { findings, seen };
}

function gapFindings<S extends string>(
  seen: ReadonlySet<number>,
  source: S
): Finding<ListRule, S>[] {
  const findings: Finding<ListRule, S>[] = [];
  const lowest = Math.min(...seen);
  const highest = Math.max(...seen);
  // A list that starts above 1 is either missing its first references or is a pasted
  // excerpt; say it once rather than listing every number below the first.
  if (lowest > 1) {
    findings.push({
      rule: "numbering-start",
      confidence: "medium",
      source,
      values: { first: lowest },
    });
  }
  const gaps: number[] = [];
  for (let n = lowest; n <= highest; n++) {
    if (!seen.has(n)) {
      gaps.push(n);
    }
  }
  if (gaps.length > 0) {
    findings.push({
      rule: "numbering-gap",
      confidence: "high",
      source,
      values: { numbers: numberList(gaps) },
    });
  }
  return findings;
}

/**
 * The numbering itself: unnumbered entries among numbered ones, a number used twice, a
 * list that does not start at 1, a number skipped, and a number lower than the one
 * before it.
 */
export function numberingFindings<S extends string>(
  entries: readonly ListEntry[],
  source: S
): Finding<ListRule, S>[] {
  const numbered = entries.filter((entry) => entry.label !== null);
  if (numbered.length === 0) {
    return entries.length > 1
      ? [{ rule: "unnumbered", confidence: "medium", source }]
      : [];
  }
  const findings: Finding<ListRule, S>[] = [];
  const missing = entries
    .filter((entry) => entry.label === null)
    .map((entry) => entry.position);
  if (missing.length > 0) {
    findings.push({
      rule: "numbering-missing",
      confidence: "high",
      source,
      values: { entries: numberList(missing) },
    });
  }
  const sequence = sequenceFindings(numbered, source);
  findings.push(...sequence.findings, ...gapFindings(sequence.seen, source));
  return findings;
}

const RE_KEY_STRIP = /[^\p{L}\p{N}]+/gu;
const RE_DOI_CORE = /\b(10\.\d{4,9}\/[^\s"<>]+?)(?=[.,;)]*(?:\s|$))/i;

/** The DOI's own text, lower-cased, wherever and however it is written. */
export function doiKey(text: string): string | null {
  const match = RE_DOI_CORE.exec(text);
  return match ? match[1].toLowerCase() : null;
}

/** The same source listed twice: identical text, or the same DOI. */
export function duplicateFindings<S extends string>(
  entries: readonly ListEntry[],
  source: S
): Finding<ListRule, S>[] {
  const findings: Finding<ListRule, S>[] = [];
  const byText = new Map<string, ListEntry>();
  const byDoi = new Map<string, ListEntry>();
  for (const entry of entries) {
    const key = entry.text.toLowerCase().replace(RE_KEY_STRIP, "");
    const doi = doiKey(entry.text);
    const first =
      (key.length > 12 ? byText.get(key) : undefined) ??
      (doi ? byDoi.get(doi) : undefined);
    if (first) {
      findings.push({
        rule: "duplicate-entry",
        confidence: "high",
        source,
        values: { first: entryName(first), second: entryName(entry) },
      });
      continue;
    }
    if (key.length > 12) {
      byText.set(key, entry);
    }
    if (doi) {
      byDoi.set(doi, entry);
    }
  }
  return findings;
}

/**
 * A numbered list in alphabetical order is usually an author–date list renumbered: the
 * numbered styles order references by first citation. Five or more entries all in A–Z
 * order of first author is unlikely by chance (1 in 120), so that is the threshold.
 */
export function alphabeticalFinding<S extends string>(
  surnames: readonly (string | null)[],
  source: S
): Finding<ListRule, S> | null {
  const keys = surnames.filter((name): name is string => Boolean(name));
  if (keys.length < 5 || keys.length < surnames.length) {
    return null;
  }
  if (new Set(keys).size < 3) {
    return null;
  }
  for (let i = 1; i < keys.length; i++) {
    if (keys[i - 1].localeCompare(keys[i], "en", { sensitivity: "base" }) > 0) {
      return null;
    }
  }
  return { rule: "alphabetical-order", confidence: "medium", source };
}

/** Total findings across a report's entries and list. */
export function finishReport<R extends string, S extends string>(
  entries: EntryReport<R, S>[],
  listFindings: Finding<R, S>[]
): CheckReport<R, S> {
  return {
    entries,
    listFindings,
    totalEntries: entries.length,
    totalFindings:
      listFindings.length +
      entries.reduce((sum, entry) => sum + entry.findings.length, 0),
  };
}

// --- shared text tests -------------------------------------------------------------

const RE_ENDS_WITH_URL = /https?:\/\/\S+$/i;
const RE_ENDS_WITH_DOI = /\b10\.\d{4,9}\/\S+$/;
const RE_TERMINAL = /[.?!]["”’)]*$/;

/** The entry ends in a URL or a DOI — the place a closing period is optional or wrong. */
export function endsWithLink(text: string): boolean {
  const trimmed = text.trim();
  return RE_ENDS_WITH_URL.test(trimmed) || RE_ENDS_WITH_DOI.test(trimmed);
}

export function endsWithTerminal(text: string): boolean {
  return RE_TERMINAL.test(text.trim());
}

/**
 * Words a sentence-case title keeps lowercase but a Title Case one capitalizes only when
 * they are long — function words of four letters or more.
 */
const LONG_FUNCTION_WORDS = new Set([
  "about",
  "above",
  "across",
  "after",
  "against",
  "along",
  "among",
  "around",
  "before",
  "behind",
  "below",
  "beneath",
  "beside",
  "between",
  "beyond",
  "during",
  "except",
  "from",
  "inside",
  "into",
  "near",
  "onto",
  "over",
  "than",
  "that",
  "their",
  "these",
  "this",
  "those",
  "through",
  "toward",
  "towards",
  "under",
  "until",
  "upon",
  "with",
  "within",
  "without",
]);

const RE_WORDS = /\s+/;
const RE_LETTERS_ONLY = /[^\p{L}]/gu;
const RE_UPPER_START = /^\p{Lu}/u;
const RE_ACRONYM = /^\p{Lu}[\p{Lu}\d-]+$/u;
const RE_EDGE_PUNCT = /^[^\p{L}]+|[^\p{L}]+$/gu;
const RE_DIGITS = /^\d+$/;

/**
 * A title typed in Title Case where the style wants sentence case.
 *
 * Deliberately narrow, like the APA checker's version: it fires only when at least
 * three content words (four letters or more, not acronyms, after the first word) appear
 * and every one of them is capitalized. A sentence-case title with a few proper nouns
 * keeps its ordinary words lowercase and passes.
 */
export function looksTitleCase(title: string): boolean {
  const words = title
    .trim()
    .split(RE_WORDS)
    .slice(1)
    .map((word) => word.replace(RE_EDGE_PUNCT, ""))
    .filter((word) => word.replace(RE_LETTERS_ONLY, "").length >= 4)
    .filter((word) => !RE_ACRONYM.test(word))
    .filter((word) => !LONG_FUNCTION_WORDS.has(word.toLowerCase()));
  return words.length >= 3 && words.every((word) => RE_UPPER_START.test(word));
}

// --- page ranges -------------------------------------------------------------------

/**
 * NLM's short form of a page range: drop the leading digits the last page shares with
 * the first. 123–125 → 123-5, 1563–1571 → 1563-71. Null when there is nothing to drop
 * (different lengths, or not plain numbers).
 */
export function elidedRange(first: string, last: string): string | null {
  if (!(RE_DIGITS.test(first) && RE_DIGITS.test(last))) {
    return null;
  }
  if (first.length !== last.length || Number(last) <= Number(first)) {
    return null;
  }
  let shared = 0;
  while (shared < first.length - 1 && first[shared] === last[shared]) {
    shared++;
  }
  return shared === 0 ? null : `${first}-${last.slice(shared)}`;
}

/**
 * AMA's full form of an elided range: 1563–71 → 1563-1571. Null when the range is not
 * elided or the expansion would not come after the first page.
 */
export function fullRange(first: string, last: string): string | null {
  if (!(RE_DIGITS.test(first) && RE_DIGITS.test(last))) {
    return null;
  }
  if (last.length >= first.length) {
    return null;
  }
  const expanded = first.slice(0, first.length - last.length) + last;
  return Number(expanded) > Number(first) ? `${first}-${expanded}` : null;
}
